import { motion } from "framer-motion";

const GRADIENTS = {
  morning: "from-[#AEE3F7] via-[#DFF3FB] to-[#FFF4E3]",
  afternoon: "from-[#8FD3F2] via-[#CBEBF8] to-[#FFEDD5]",
  evening: "from-[#565B9B] via-[#9C7FB8] to-[#FFC79E]",
};
const SUN = { morning: "#FFD166", afternoon: "#FFC94D", evening: "#FF9E5E" };

function Cloud({ className, style, drift = 0, duration = 12 }) {
  return (
    <motion.svg
      viewBox="0 0 100 44"
      className={className}
      style={style}
      animate={drift ? { x: [0, drift, 0] } : {}}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      aria-hidden
    >
      <ellipse cx="30" cy="32" rx="26" ry="12" fill="currentColor" />
      <ellipse cx="70" cy="32" rx="26" ry="12" fill="currentColor" />
      <circle cx="40" cy="18" r="16" fill="currentColor" />
      <circle cx="62" cy="22" r="13" fill="currentColor" />
    </motion.svg>
  );
}

function Bird({ delay = 0, top = "16%", duration = 26 }) {
  return (
    <motion.svg
      viewBox="0 0 40 20"
      className="absolute h-3 w-6 sm:h-4 sm:w-8"
      style={{ top, left: "-10%" }}
      initial={{ x: 0 }}
      animate={{ x: "120vw" }}
      transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
      aria-hidden
    >
      <path d="M2 12 Q10 2 20 12 Q30 2 38 12" stroke="#2B2D42" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.55" />
    </motion.svg>
  );
}

export function Sky({ period, awake, reduce }) {
  const sun = SUN[period];
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden bg-gradient-to-b ${GRADIENTS[period]}`}>
      {/* sun rises when the world wakes */}
      <motion.div
        className={`absolute right-[10%] ${period === "evening" ? "top-[24%]" : "top-[7%]"}`}
        initial={{ y: 280, opacity: 0 }}
        animate={awake ? { y: 0, opacity: 1 } : { y: 280, opacity: 0 }}
        transition={reduce ? { duration: 0 } : { duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="relative">
          <div className="absolute -inset-8 rounded-full blur-2xl" style={{ background: `${sun}55` }} />
          <div className="absolute -inset-3 rounded-full blur-md" style={{ background: `${sun}88` }} />
          <div className="relative h-16 w-16 rounded-full sm:h-24 sm:w-24" style={{ background: sun }} />
        </div>
      </motion.div>

      {period === "evening" && (
        <>
          {[["12%", "10%"], ["30%", "6%"], ["48%", "14%"], ["70%", "5%"], ["86%", "12%"]].map(([left, top], i) => (
            <motion.span
              key={i}
              className="absolute h-1.5 w-1.5 rounded-full bg-white/90"
              style={{ left, top }}
              animate={reduce ? {} : { opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 2.4, delay: i * 0.5, repeat: Infinity }}
            />
          ))}
        </>
      )}

      {/* drifting puffy clouds */}
      <Cloud className="absolute left-[8%] top-[8%] w-24 text-white/90 sm:w-32" drift={reduce ? 0 : 24} duration={12} />
      <Cloud className="absolute left-[46%] top-[5%] w-16 text-white/75 sm:w-24" drift={reduce ? 0 : -18} duration={15} />
      <Cloud className="absolute left-[72%] top-[19%] w-14 text-white/60 sm:w-20" drift={reduce ? 0 : 16} duration={18} />

      {/* birds appear once the world is awake */}
      {awake && !reduce && (
        <>
          <Bird top="14%" duration={30} />
          <Bird top="22%" delay={6} duration={38} />
        </>
      )}
    </div>
  );
}
